import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Progress } from 'components/ui/progress';
import { Separator } from 'components/ui/separator';
import {
  selectPrintQueue,
  selectPrintProgress,
  selectIsProcessing,
  selectPrintError,
  selectPrintWarnings,
  selectActivePrintJobId,
  selectPrintRetryCounts,
} from 'services/printing/redux/selectors';
import { PrintJob } from 'services/printing/types';
import { getJobOrderSummary } from 'services/printing/utils/getJobOrderSummary';
import { Workstation } from 'app/containers/OrderDetails/types';
import { Button } from 'components/ui/button';
import { useDispatch } from 'react-redux';
import {
  setProcessing,
  setError,
  clearQueue,
  removeJobFromQueue,
} from 'services/printing/redux/printingSlice';
import { ElectronPrintingApi } from 'services/printing/renderer/ElectronPrintingApi';
import { FiTrash2 } from 'react-icons/fi';

const PrintQueueViewer: React.FC = () => {
  const dispatch = useDispatch();
  const queue = useSelector(selectPrintQueue);
  const progress = useSelector(selectPrintProgress) ?? {
    completed: 0,
    total: 0,
    percentage: 0,
  };
  const isProcessing = useSelector(selectIsProcessing);
  const error = useSelector(selectPrintError);
  const warnings = useSelector(selectPrintWarnings) ?? [];
  const activeJobId = useSelector(selectActivePrintJobId);
  const retryCounts = useSelector(selectPrintRetryCounts) ?? {};
  const [retrying, setRetrying] = useState(false);

  // Hooks must be called unconditionally; ensure no early return before hooks.
  // Define helpers and callbacks first, then conditionally render.

  const getJobTypeLabel = (type: PrintJob['type']) => {
    const labels = {
      picking: 'Picking',
      label: 'Étiquette',
      'carrier-page': 'Page transporteur',
      assembly: 'Notice',
    };
    return labels[type];
  };

  const getJobTypeColor = (type: PrintJob['type']) => {
    const colors = {
      picking: 'bg-blue-100 text-blue-800',
      label: 'bg-green-100 text-green-800',
      'carrier-page': 'bg-purple-100 text-purple-800',
      assembly: 'bg-orange-100 text-orange-800',
    };
    return colors[type];
  };

  const getWorkstationLabel = (workstation?: Workstation) => {
    if (workstation === undefined) return '';
    const labels = {
      [Workstation.Cabinet]: 'Meuble',
      [Workstation.Corner]: 'Corner',
      [Workstation.Drawer]: 'Tiroirs',
      [Workstation.Accessory]: 'Accessoires',
      [Workstation.Woodwork]: 'Menuiserie',
      [Workstation.Marketing]: 'Marketing',
      [Workstation.WoodworkSpecial]: 'Menuiserie spéciale',
    };
    return labels[workstation];
  };

  const handleClearQueue = useCallback(() => {
    dispatch(clearQueue());
  }, [dispatch]);

  const handleCancelJob = useCallback(
    (jobId: string) => {
      dispatch(removeJobFromQueue(jobId));
    },
    [dispatch],
  );

  const handleRetry = useCallback(async () => {
    if (!queue || (queue.jobs?.length ?? 0) === 0) return;
    setRetrying(true);
    dispatch(setProcessing(true));
    try {
      const available = await ElectronPrintingApi.isAvailable();
      if (!available) {
        dispatch(
          setError(
            "Impression Electron indisponible. Vérifiez l'environnement.",
          ),
        );
        return;
      }
      const config = ElectronPrintingApi.createDefaultConfig();
      const connection = await ElectronPrintingApi.connect(config);
      if (!connection.connected) {
        const message =
          connection.error ??
          'Connexion imprimante impossible. Réessayez plus tard.';
        dispatch(setError(message));
        return;
      }
      const result = await ElectronPrintingApi.print({
        jobs: queue.jobs ?? [],
        printerConfig: config,
      });
      if (!result.success) {
        dispatch(
          setError(
            'Aucun travail soumis. Corrigez les erreurs puis réessayez.',
          ),
        );
      }
    } catch (e) {
      console.error('Retry failed', e);
      dispatch(setError('Erreur lors de la relance de la file.'));
    } finally {
      setRetrying(false);
      dispatch(setProcessing(false));
    }
  }, [queue, dispatch]);

  // Safe early return AFTER all hooks are declared
  if (!queue) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>File d'impression</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Aucun travail en file</p>
        </CardContent>
      </Card>
    );
  }

  const jobs = queue.jobs ?? [];

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          File d'impression
          <Badge variant={isProcessing ? 'default' : 'secondary'}>
            {isProcessing ? 'En cours' : 'En attente'}
          </Badge>
        </CardTitle>
        {jobs.length > 0 && (
          <div className="mt-2 flex justify-end gap-2">
            <Button size="sm" variant="destructive" onClick={handleClearQueue}>
              <FiTrash2 className="mr-2 h-4 w-4" />
              Vider la file
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleRetry}
              disabled={isProcessing || retrying}
            >
              {retrying ? 'Relance…' : "Relancer l'impression"}
            </Button>
          </div>
        )}

        {progress.total > 0 && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>
                {progress.completed} / {progress.total} travaux
              </span>
              <span>{progress.percentage}%</span>
            </div>
            <Progress value={progress.percentage} className="w-full" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-3">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}
      </CardHeader>

      <CardContent>
        <div className="space-y-3">
          <div className="flex justify-between text-sm font-medium">
            <span>Stratégie: {queue.groupingStrategy}</span>
            <span>{queue.totalPages ?? 0} pages total</span>
          </div>

          <Separator />

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {jobs.map((job, index) => {
              const isActive = job.id === activeJobId;
              const retryCount = retryCounts?.[job.id] ?? 0;
              const { orderId, customerDisplayName } = getJobOrderSummary(job);
              const orderLabel = orderId ? `Order #${orderId}` : '';
              const hasHeadline = Boolean(orderLabel || customerDisplayName);
              const detailParts = [
                `${job.metadata.pageCount} page(s)`,
                job.metadata.format,
              ].filter(Boolean);
              return (
                <div
                  key={job.id}
                  className={`flex items-center justify-between p-2 border rounded ${
                    isActive ? 'border-blue-500 bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-mono text-gray-500">
                      #{index + 1}
                    </span>
                    <div>
                      <div className="flex items-center space-x-2">
                        <Badge className={getJobTypeColor(job.type)}>
                          {getJobTypeLabel(job.type)}
                        </Badge>
                        {job.workstation !== undefined && (
                          <Badge variant="outline">
                            {getWorkstationLabel(job.workstation)}
                          </Badge>
                        )}
                        {Array.isArray(job.workstations) &&
                          job.workstations.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {job.workstations.map((ws, index) => (
                                <Badge
                                  key={`${job.id}-ws-${index}`}
                                  variant="outline"
                                >
                                  {getWorkstationLabel(ws)}
                                </Badge>
                              ))}
                            </div>
                          )}
                      </div>
                      <p className="text-xs text-gray-600">
                        {orderLabel && <span>{orderLabel}</span>}
                        {customerDisplayName && (
                          <>
                            {orderLabel ? ' • ' : ''}
                            <span className="font-semibold">
                              {customerDisplayName}
                            </span>
                          </>
                        )}
                        {detailParts.length > 0 && (
                          <>
                            {hasHeadline ? ' • ' : ''}
                            {detailParts.join(' • ')}
                          </>
                        )}
                      </p>
                      {(job.metadata.requiresFolding ||
                        job.metadata.requiresStapling) && (
                        <p className="text-xs text-blue-600">
                          {job.metadata.requiresFolding && 'Pliage'}
                          {job.metadata.requiresFolding &&
                            job.metadata.requiresStapling &&
                            ' + '}
                          {job.metadata.requiresStapling && 'Agrafage'}
                        </p>
                      )}
                      <div className="flex items-center space-x-2 mt-1">
                        {isActive && <Badge variant="outline">En cours</Badge>}
                        {retryCount > 0 && (
                          <Badge variant="destructive">
                            {retryCount} retry
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <p className="text-sm font-medium">
                      {job.metadata.groupKey}
                    </p>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleCancelJob(job.id)}
                      title="Retirer ce travail de la file"
                    >
                      <FiTrash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {warnings.length > 0 && (
          <div className="mt-4 border-t pt-3">
            <h4 className="text-sm font-semibold text-amber-800">Warnings</h4>
            <ul className="mt-2 space-y-1 text-sm text-amber-900">
              {warnings.map((warning, index) => (
                <li key={`${warning}-${index}`}>{warning}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PrintQueueViewer;
